import { ArrowUpRight } from "lucide-react";
import { isDesktop, openExternal } from "../lib/desktop.ts";

/**
 * The Academy pages the app links to. A mirror of the `usedBy: ["app"]` entries in
 * apps/marketing/lib/academy-links.ts (same keys, same paths): the site's build checks those, and
 * tools/academy-phase2.test.mjs fails when a path here isn't listed there.
 */
export const ACADEMY = {
  home: "/academy",
  firstPlan: "/academy/start/first-plan",
  bringYourBlocks: "/academy/start/bring-your-blocks",
  findTheJar: "/academy/start/bring-your-blocks#find-the-jar",
  packsAndMods: "/academy/start/bring-your-blocks#packs-and-mods",
  openYourWorld: "/academy/start/open-your-world",
  whereAreWorlds: "/academy/start/open-your-world#where-are-my-worlds",
  demoIsland: "/academy/start/demo-island",
  troubleshooting: "/academy/help/troubleshooting",
  worldWontOpen: "/academy/help/troubleshooting#world-wont-open",
  noTextures: "/academy/help/troubleshooting#no-textures",
  appSignIn: "/academy/help/troubleshooting#sign-in",
  crashReport: "/academy/help/troubleshooting#crash-report",
  feedback: "/academy/help/faq#feedback",
  schematicsImport: "/academy/schematics/import",
  shareAWorld: "/academy/together/share-a-world",
  inviteRoles: "/academy/together/invite-roles#roles",
  plansCompared: "/academy/account/plans",
  liveWorlds: "/academy/account/upgrade-downgrade#live-worlds",
} as const;

export type AcademyKey = keyof typeof ACADEMY;

/** https://www.mc-planner.com/academy/…?ref=app#anchor (the same shape as academyUrl() on the site) */
export function academyUrl(key: AcademyKey): string {
  const [path, anchor] = ACADEMY[key].split("#");
  return `https://www.mc-planner.com${path}?ref=app${anchor ? `#${anchor}` : ""}`;
}

/**
 * A small "Learn more" link (with a lucide arrow) into the Academy. Opens a new tab in the browser, the system browser in the desktop app.
 * `about` completes the accessible name ("Learn more about sharing a world"), since several panels say "Learn more".
 * Clicks don't bubble, so it can sit inside something clickable (the error toast).
 */
export function AcademyLink({ to, about, children = "Learn more", className }: { to: AcademyKey; about?: string; children?: React.ReactNode; className?: string }) {
  const href = academyUrl(to);
  const desktop = isDesktop();
  const label = typeof children === "string" ? `${children}${about ? ` about ${about}` : ""} (Academy, opens in your browser)` : undefined;
  return (
    <a
      className={`learn-more${className ? " " + className : ""}`}
      href={href}
      target="_blank"
      rel="noopener"
      aria-label={label}
      onClick={(e) => { e.stopPropagation(); if (desktop) { e.preventDefault(); void openExternal(href); } }}
    >
      {children}<ArrowUpRight className="trail" size="1em" aria-hidden="true" />
    </a>
  );
}
